import React, { useState } from 'react';
import PropTypes from 'prop-types';

import CourseHead from './CourseHead';
import CopyPayment from '../CopyPayment/CopyPayment';
import { ArrowRight, CapIcon, ChevronDown, DownloadIcon, WalletIcon } from './icons';
import { lessonsWord, formatMoney } from '../../utils/lessonsWord';

import './finances-v2.scss';
import './accounts-v2.scss';

const isActual = (count) => count.show_button_pay === true && !count.total_price;
const isPaid = (count) => count.show_button_pay === false && Boolean(count.total_price);

const PaidRow = ({ count, getPdf }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`av2-paid ${open ? 'is-open' : ''}`.trim()}>
      <button type="button" className="av2-paid__row" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span className="av2-paid__main">
          <span className="av2-paid__date">{count.payment_date || count.created_at}</span>
          <span className="av2-paid__name">
            {count.image && <img src={count.image} alt="" width="30" height="20" decoding="async" />}
            <span>{count.nomenclature_name}</span>
          </span>
          {count.qty && (
            <span className="av2-paid__meta">
              {count.qty} {lessonsWord(count.qty)} · {formatMoney(count.price)} грн/урок
            </span>
          )}
        </span>
        <span className="av2-paid__side">
          <span className="v2-chip v2-chip--green">Оплачено</span>
          <b className="av2-paid__sum">{formatMoney(count.total_price)} грн</b>
        </span>
        <ChevronDown className="av2-paid__chev" />
      </button>

      {open && (
        <div className="av2-paid__more">
          <div className="av2-doc">
            <span className="av2-doc__label">Рахунок-договір:</span>
            {count.document_url
              ? <button type="button" className="av2-doc__link" onClick={() => window.open(count.document_url, '_blank', 'noopener')}>{count.document_name}</button>
              : <span className="av2-doc__link">{count.document_name}</span>}
            {count.show_document_url && (
              <button type="button" className="av2-doc__icon" aria-label="Завантажити" onClick={() => getPdf(count.id)}>
                <DownloadIcon />
              </button>
            )}
          </div>
          {count.checkbox_url && (
            <button type="button" className="v2-link" onClick={() => window.open(count.checkbox_url, '_blank', 'noopener')}>
              Фіскальний чек
            </button>
          )}
        </div>
      )}
    </div>
  );
};

PaidRow.propTypes = {
  count: PropTypes.object.isRequired,
  getPdf: PropTypes.func.isRequired,
};

const AccountsV2 = ({ accounts, course, studentBtn, message, onCreate, onPay, getPdf, onHistory, isDemo }) => {
  const [paidOpen, setPaidOpen] = useState(true);
  const [oldOpen, setOldOpen] = useState(false);

  const actual = accounts.find(isActual);
  const paid = accounts.filter(isPaid);
  const old = accounts.filter((count) => !isActual(count) && !isPaid(count));

  const left = Number(course?.lessons_left) || 0;
  const total = Number(course?.lessons_total) || 0;
  const passed = Number(course?.lessons_passed) || 0;
  const percent = total ? Math.min(100, Math.round((passed / total) * 100)) : 0;

  return (
    <div className="av2">
      {actual ? (
        <article className="fv2-card av2-actual">
          <div className="av2-actual__top">
            <span className="v2-chip v2-chip--green">Актуальний рахунок</span>
          </div>
          <span className="av2-actual__date">{actual.created_at}</span>

          <CourseHead
            image={actual.image || course?.image}
            title={course?.direction_name || actual.nomenclature_name}
            programName={course?.program_name}
            type={course?.type}
          />

          {total > 0 && (
            <div className="av2-progress">
              <span className="av2-progress__icon"><CapIcon /></span>
              <span className="av2-progress__text">
                {left > 0 ? 'До завершення курсу залишилося' : 'Курс пройдено'}
                {left > 0 && <b>{left} {lessonsWord(left)}</b>}
              </span>
              <span className="av2-progress__bar">
                <span className="av2-progress__pct">{percent}% пройдено</span>
                <span className="v2-progress__track"><i style={{ width: `${percent}%` }}></i></span>
              </span>
            </div>
          )}

          <div className="av2-doc">
            <span className="av2-doc__label">Рахунок-договір:</span>
            {actual.document_url
              ? <button type="button" className="av2-doc__link" onClick={() => window.open(actual.document_url, '_blank', 'noopener')}>{actual.document_name}</button>
              : <span className="av2-doc__link">{actual.document_name}</span>}
            {actual.show_document_url && (
              <button type="button" className="av2-doc__icon" aria-label="Завантажити" onClick={() => getPdf(actual.id)}>
                <DownloadIcon />
              </button>
            )}
            {!isDemo && actual.show_button_pay && (
              <span className="av2-doc__copy">
                <CopyPayment validTime={actual.valid_seconds} id={actual.id} url={actual.payment_url} />
              </span>
            )}
          </div>

          <div className="fv2-actions">
            <button type="button" className="v2-btn v2-btn--green" onClick={() => onPay(actual)}>
              <WalletIcon />
              Поповнити баланс
              <ArrowRight />
            </button>
            <button type="button" className="v2-link" onClick={onHistory}>Історія оплат і занять</button>
          </div>
        </article>
      ) : (
        <div className="fv2-card av2-empty">
          <p className="av2-empty__text">
            {message || (studentBtn
              ? 'Актуального рахунку немає. Створіть новий — він одразу з’явиться тут.'
              : 'Рахунок ще не сформовано. Для його створення, будь ласка, зв’яжіться з менеджером у чат-боті.')}
          </p>
          {studentBtn && !message && (
            <button type="button" className="v2-btn v2-btn--green" onClick={onCreate}>Створити рахунок</button>
          )}
        </div>
      )}

      {paid.length > 0 && (
        <section className={`v2-section ${paidOpen ? '' : 'is-closed'}`.trim()}>
          <button type="button" className="v2-section__head" aria-expanded={paidOpen} onClick={() => setPaidOpen((value) => !value)}>
            <h2 className="v2-section__title">Оплачені рахунки ({paid.length})</h2>
            <ChevronDown className="v2-section__chev" />
          </button>
          {paidOpen && <div className="av2-list">{paid.map((count) => <PaidRow key={count.id} count={count} getPdf={getPdf} />)}</div>}
        </section>
      )}

      {old.length > 0 && (
        <section className={`v2-section ${oldOpen ? '' : 'is-closed'}`.trim()}>
          <button type="button" className="v2-section__head" aria-expanded={oldOpen} onClick={() => setOldOpen((value) => !value)}>
            <h2 className="v2-section__title">Неактуальні рахунки ({old.length})</h2>
            <ChevronDown className="v2-section__chev" />
          </button>
          {oldOpen && (
            <div className="av2-list">
              {old.map((count) => (
                <div key={count.id} className="av2-paid av2-paid--old">
                  <div className="av2-paid__row">
                    <span className="av2-paid__main">
                      <span className="av2-paid__date">{count.created_at}</span>
                      <span className="av2-paid__name"><span>{count.document_name}</span></span>
                    </span>
                    <span className="av2-paid__side"><span className="v2-chip">Не актуальний</span></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

AccountsV2.propTypes = {
  accounts: PropTypes.array.isRequired,
  course: PropTypes.object,
  studentBtn: PropTypes.bool,
  message: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
  onPay: PropTypes.func.isRequired,
  getPdf: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
  isDemo: PropTypes.bool,
};

AccountsV2.defaultProps = {
  course: null,
  studentBtn: false,
  message: '',
  isDemo: false,
};

export default AccountsV2;
